import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	forwardRef,
	inject,
	Input,
	OnInit,
} from "@angular/core";
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from "@angular/forms";
import { BehaviorSubject } from "rxjs";

import { getFormattedAddressLabel } from "@intocare/pipes";

import { addressValidator, countryCodeValidator } from "./../../validators";
import { AddressComponent, GeolocationResult, LocationResult } from "../../entities/geolocation";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => LocationInputComponent),
	multi: true,
};

@Component({
	selector: "ic-location-input",
	templateUrl: "./input.component.html",
	providers: [INPUT_CONTROL_ACCESSOR],
	styleUrls: ["./input.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LocationInputComponent implements OnInit, ControlValueAccessor {
	private fb = inject(UntypedFormBuilder);
	private cdRef = inject(ChangeDetectorRef);

	@Input() type: string;
	@Input() label: string;
	@Input() placeholder = "";
	@Input() icon: string;
	@Input() inputClass: string;
	@Input() help = "";
	@Input() required = true;
	@Input() missing = false;
	@Input() missingText: string;
	@Input() validateCountryCode = true;

	form: UntypedFormGroup;
	showAddressFormSubject$ = new BehaviorSubject(false);
	showAddressForm$ = this.showAddressFormSubject$.asObservable();
	address: LocationResult;

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onChange = (_: unknown) => {};

	updateForm(address: LocationResult) {
		const text = getFormattedAddressLabel(address, "street number{/box}, zip city, country");
		this.form.patchValue({ address, text });

		if (this.form.valid || this.form.hasError("noCountryCode")) {
			return;
		}

		this.showForm(address);
	}

	writeValue(address: LocationResult) {
		this.resetForm();
		if (address) {
			this.updateForm(address);
		}
		this.cdRef.markForCheck();
	}

	showForm(address: LocationResult) {
		this.address = address;
		this.showAddressFormSubject$.next(true);
		this.cdRef.detectChanges();
	}

	hideForm() {
		this.showAddressFormSubject$.next(false);
		this.cdRef.detectChanges();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		if (isDisabled) {
			this.form.disable();
		} else {
			this.form.enable();
		}
		this.cdRef.detectChanges();
	}

	onBlur() {
		if (this.form.hasError("incompleteAddress")) {
			this.form.patchValue({ text: "" });
			return;
		}
		const text = getFormattedAddressLabel(this.form.value.address, "street number{/box}, zip city, country");
		this.form.patchValue({ text });
	}

	resetForm() {
		this.form.reset();
		this.hideForm();
	}

	clear() {
		this.resetForm();
		this.onChange(null);
		this.onTouch();
	}

	placeChangedCallback(value: GeolocationResult) {
		if (!value.address_components) {
			this.resetForm();
			return;
		}
		const locations = value.address_components;

		const result = {
			street: this.getLongName(locations, "route"),
			number: this.getLongName(locations, "street_number"),
			city: this.getLongName(locations, "locality"),
			state:
				this.getLongName(locations, "administrative_area_level_2")
				|| this.getLongName(locations, "administrative_area_level_1"),
			country: this.getLongName(locations, "country"),
			zip: this.getLongName(locations, "postal_code"),
			countryCode: this.getShortName(locations, "country"),
		};

		this.updateForm(result);
		if (this.form.valid) {
			this.onChange(this.form.getRawValue().address);
			this.onTouch();
		}
	}

	onLocationFormSave(location: LocationResult) {
		this.hideForm();
		this.updateForm(location);
		this.onChange(this.form.getRawValue().address);
		this.onTouch();
	}

	private getLongName(locations: AddressComponent[], search: string) {
		const value = locations.find((location) => location.types.includes(search));
		return value ? value.long_name : "";
	}

	private getShortName(locations: AddressComponent[], search: string) {
		const value = locations.find((location) => location.types.includes(search));

		return value ? value.short_name : "";
	}

	private initForm() {
		let addressValidators;

		addressValidators = [addressValidator];

		if (this.validateCountryCode) {
			addressValidators = [addressValidator, countryCodeValidator];
		}

		this.form = this.fb.group(
			{
				address: [null, [Validators.required]],
				text: [""],
			},
			{ validators: addressValidators },
		);
	}

	ngOnInit() {
		this.initForm();
	}
}
